import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { clearErrors, getProductDetails, newReview } from '../../actions/productAction';
import ProductSlider from '../Home/ProductSlider/ProductSlider';
import Loader from '../Layouts/Loader';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Rating from '@mui/material/Rating';
import TextField from '@mui/material/TextField';
import { NEW_REVIEW_RESET } from '../../constants/productConstants';
import { addItemsToCart } from '../../actions/cartAction';
import { getDiscount } from '../../utils/functions';
import { addToWishlist, removeFromWishlist } from '../../actions/wishlistAction';
import MetaData from '../Layouts/MetaData';
import { useLang, uiText } from '../../utils/i18n';

const ProductDetails = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const params = useParams();
    const navigate = useNavigate();
    const { lang } = useLang();

    const [open, setOpen] = useState(false);
    const [viewAll, setViewAll] = useState(false);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");

    const [selectedImage, setSelectedImage] = useState(0);

    const [similarByCategory, setSimilarByCategory] = useState([]);
    const [similarByBrand, setSimilarByBrand] = useState([]);
    const [offers, setOffers] = useState([]);

    const { product, loading, error } = useSelector((state) => state.productDetails);
    const { success, error: reviewError } = useSelector((state) => state.newReview);
    const { cartItems } = useSelector((state) => state.cart);
    const { wishlistItems } = useSelector((state) => state.wishlist);

    const displayName = lang === 'zh' && product?.nameZh ? product.nameZh : product?.name;
    const displayDescription = lang === 'zh' && product?.descriptionZh ? product.descriptionZh : product?.description;
    const displayShortDescription = lang === 'zh' && product?.shortDescriptionZh ? product.shortDescriptionZh : product?.shortDescription;

    const productId = params.id;
    const itemInWishlist = wishlistItems.some((i) => i.product === productId);

    const addToWishlistHandler = () => {
        if (itemInWishlist) {
            dispatch(removeFromWishlist(productId));
            enqueueSnackbar(uiText('removeFromWishlist', lang), { variant: "success" });
        } else {
            dispatch(addToWishlist(productId));
            enqueueSnackbar(uiText('addedToWishlist', lang), { variant: "success" });
        }
    }

    const reviewSubmitHandler = () => {
        if (rating === 0 || !comment.trim()) {
            enqueueSnackbar(uiText('emptyReview', lang), { variant: "error" });
            return;
        }
        const formData = new FormData();
        formData.set("rating", rating);
        formData.set("comment", comment);
        formData.set("productId", productId);
        dispatch(newReview(formData));
        setOpen(false);
    }

    const addToCartHandler = () => {
        dispatch(addItemsToCart(productId));
        enqueueSnackbar(uiText('productAddedToCart', lang), { variant: "success" });
    }

    const handleDialogClose = () => {
        setOpen(!open);
    }

    const itemInCart = cartItems.some((i) => i.product === productId);

    const goToCart = () => {
        navigate('/cart');
    }

    const buyNow = () => {
        addToCartHandler();
        navigate('/shipping');
    }

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (reviewError) {
            enqueueSnackbar(reviewError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (success) {
            enqueueSnackbar(uiText('reviewSubmitted', lang), { variant: "success" });
            dispatch({ type: NEW_REVIEW_RESET });
        }
        dispatch(getProductDetails(productId));
        setSelectedImage(0);
        // eslint-disable-next-line
    }, [dispatch, productId, error, reviewError, success, enqueueSnackbar]);

    useEffect(() => {
        if (!product?._id || product._id !== productId) return;

        const fetchByCategory = async () => {
            const slug = product.categories?.[0]?.slug;
            if (!slug) return;
            try {
                const { data } = await axios.get(`/api/v1/products?category=${slug}`);
                setSimilarByCategory((data.products || []).filter((p) => p._id !== productId).slice(0, 12));
            } catch (err) {
                console.error('Failed to load similar products by category', err);
            }
        };

        const fetchByBrand = async () => {
            const brandName = product.brand?.name;
            if (!brandName) return;
            try {
                const { data } = await axios.get(`/api/v1/products?brand=${encodeURIComponent(brandName)}`);
                setSimilarByBrand((data.products || []).filter((p) => p._id !== productId).slice(0, 12));
            } catch (err) {
                console.error('Failed to load similar products by brand', err);
            }
        };

        fetchByCategory();
        fetchByBrand();

        const fetchOffers = async () => {
            try {
                const { data } = await axios.get(`/api/v1/product-offers?product=${productId}`);
                setOffers(data.offers || []);
            } catch (err) {
                console.error('Failed to load offers', err);
            }
        };
        fetchOffers();
    }, [product, productId]);

    return (
        <>
            {loading ? <Loader /> : (
                <>
                    <MetaData title={displayName} />
                    <main className="mt-12 sm:mt-0">

                        <div className="w-full flex flex-col sm:flex-row bg-white sm:p-2 relative">

                            <div className="w-full sm:w-2/5 sm:sticky top-16 sm:h-screen">
                                <div className="flex flex-col gap-3 m-3">

                                    <div className="w-full border relative">
                                        <img
                                            draggable="false"
                                            className="w-full h-96 object-contain"
                                            src={product.images?.[selectedImage]?.url}
                                            alt={displayName}
                                        />
                                        <div className="absolute top-4 right-4 shadow-lg bg-white w-9 h-9 border flex items-center justify-center rounded-full">
                                            <span onClick={addToWishlistHandler} className={`${itemInWishlist ? "text-red-500" : "hover:text-red-500 text-gray-300"} cursor-pointer`}><FavoriteIcon sx={{ fontSize: "18px" }} /></span>
                                        </div>
                                    </div>

                                    {product.images && product.images.length > 1 && (
                                        <div className="flex gap-2 overflow-x-auto pb-1">
                                            {product.images.map((img, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => setSelectedImage(i)}
                                                    className={`w-16 h-16 flex-shrink-0 border rounded overflow-hidden ${selectedImage === i ? 'border-primary-blue border-2' : 'border-gray-200'}`}
                                                >
                                                    <img draggable="false" src={img.url} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    <div className="w-full flex gap-3">
                                        {!product.outOfStock && (
                                            <button onClick={itemInCart ? goToCart : addToCartHandler} className="p-4 w-1/2 flex items-center justify-center gap-2 text-white bg-primary-yellow rounded-sm shadow hover:shadow-lg">
                                                <ShoppingCartIcon />
                                                {itemInCart ? uiText('goToCartBtn', lang) : uiText('addToCartBtn', lang)}
                                            </button>
                                        )}
                                        <button onClick={buyNow} disabled={product.outOfStock ? true : false} className={product.outOfStock ? "p-4 w-full flex items-center justify-center gap-2 text-white bg-red-600 cursor-not-allowed rounded-sm shadow hover:shadow-lg" : "p-4 w-1/2 flex items-center justify-center gap-2 text-white bg-primary-orange rounded-sm shadow hover:shadow-lg"}>
                                            <FlashOnIcon />
                                            {product.outOfStock ? uiText('outOfStock', lang) : uiText('buyNowBtn', lang)}
                                        </button>
                                    </div>

                                </div>
                            </div>

                            <div className="flex-1 py-2 px-3">

                                <div className="flex flex-col gap-2 mb-4">

                                    <h2 className="text-xl">{displayName}</h2>
                                    <span className="text-sm text-gray-500 font-medium flex gap-2 items-center">
                                        <span className="text-xs px-1.5 py-0.5 bg-primary-green rounded-sm text-white flex items-center gap-0.5">{product.ratings && product.ratings.toFixed(1)} <StarIcon sx={{ fontSize: "12px" }} /></span>
                                        <span>{product.numOfReviews} {uiText('reviewsWord', lang)}</span>
                                    </span>

                                    <span className="text-primary-green text-sm font-medium">{uiText('specialPrice', lang)}</span>
                                    <div className="flex items-baseline gap-2 text-3xl font-medium">
                                        <span className="text-gray-800">HK${product.price?.toLocaleString()}</span>
                                        <span className="text-base text-gray-500 line-through">HK${product.cuttedPrice?.toLocaleString()}</span>
                                        <span className="text-base text-primary-green">{getDiscount(product.price, product.cuttedPrice)}%&nbsp;{uiText('offLabel', lang)}</span>
                                    </div>


                                    {offers.length > 0 && (
                                        <div className="flex flex-col gap-1 mt-2">
                                            <p className="text-md font-medium">{uiText('availableOffers', lang)}</p>
                                            {offers.map((offer) => (
                                                <p className="text-sm flex items-center gap-2" key={offer._id}>
                                                    <span className="text-primary-lightGreen font-medium">●</span> {lang === 'zh' && offer.descriptionZh ? offer.descriptionZh : offer.description}
                                                </p>
                                            ))}
                                        </div>
                                    )}

                                    {product.hasVariations && product.variations?.length > 0 && (
                                        <div className="flex flex-col gap-2 mt-2">
                                            <p className="text-sm text-gray-500 font-medium">{uiText('variationsLabel', lang)}</p>
                                            <div className="flex flex-wrap gap-3">
                                                {product.variations.map((v, i) => (
                                                    v.mode === "linked" && v.linkedProduct ? (
                                                        <Link key={i} to={`/product/${v.linkedProduct._id}`} className="border rounded-lg p-2 w-24 text-center hover:border-primary-blue">
                                                            {v.linkedProduct.images?.[0]?.url && (
                                                                <img draggable="false" src={v.linkedProduct.images[0].url} alt={v.linkedProduct.name} className="w-full h-16 object-cover rounded mb-1" />
                                                            )}
                                                            <span className="text-xs block truncate">{v.linkedProduct.name}</span>
                                                        </Link>
                                                    ) : (
                                                        <div key={i} className="border rounded-lg p-2 w-24 text-center">
                                                            {v.images?.[0]?.url && (
                                                                <img draggable="false" src={v.images[0].url} alt={v.name} className="w-full h-16 object-cover rounded mb-1" />
                                                            )}
                                                            <span className="text-xs block truncate">{v.name}</span>
                                                            {v.price && <span className="text-xs text-primary-green block">HK${v.price}</span>}
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {product.brand?.name && (
                                        <div className="flex items-center gap-3 mt-3 text-sm">
                                            <span className="text-gray-500">{uiText('brandLabelPlain', lang)}</span>
                                            <Link to={`/products?brand=${encodeURIComponent(product.brand.name)}`} className="font-medium text-primary-blue hover:underline flex items-center gap-2">
                                                {product.brand.logo?.url && (
                                                    <img draggable="false" className="h-6 object-contain" src={product.brand.logo.url} alt={product.brand.name} />
                                                )}
                                                {product.brand.name}
                                            </Link>
                                        </div>
                                    )}

                                    {displayShortDescription && (
                                        <p className="text-sm text-gray-600 mt-3">{displayShortDescription}</p>
                                    )}

                                    <div className="flex flex-col sm:flex-row gap-1 sm:gap-14 mt-4 items-stretch text-sm">
                                        <p className="text-gray-500 font-medium">{uiText('descriptionLabel', lang)}</p>
                                        <span>{displayDescription}</span>
                                    </div>

                                    <div className="w-full mt-6 rounded-sm border flex flex-col">
                                        <h1 className="px-6 py-4 border-b text-2xl font-medium">{uiText('productDescriptionHeading', lang)}</h1>
                                        <div className="p-6">
                                            <p className="text-sm">{displayDescription}</p>
                                        </div>
                                    </div>

                                    <div className="w-full mt-4 pb-4 rounded-sm border flex flex-col">
                                        <h1 className="px-6 py-4 border-b text-2xl font-medium">{uiText('specificationsHeading', lang)}</h1>
                                        <div className="px-6 py-4 text-sm">
                                            {product.additionalDetails || uiText('noAdditionalInfo', lang)}
                                        </div>
                                    </div>

                                    <div className="w-full mt-4 rounded-sm border flex flex-col">
                                        <div className="flex justify-between items-center border-b px-6 py-4">
                                            <h1 className="text-2xl font-medium">{uiText('ratingsReviewsHeading', lang)}</h1>
                                            <button onClick={handleDialogClose} className="shadow bg-primary-yellow text-white px-4 py-2 rounded-sm hover:shadow-lg">{uiText('rateProductBtn', lang)}</button>
                                        </div>

                                        <Dialog
                                            aria-labelledby='review-dialog'
                                            open={open}
                                            onClose={handleDialogClose}
                                        >
                                            <DialogTitle className="border-b">{uiText('submitReviewTitle', lang)}</DialogTitle>
                                            <DialogContent className="flex flex-col m-1 gap-4">
                                                <Rating
                                                    onChange={(e) => setRating(e.target.value)}
                                                    value={rating}
                                                    size='large'
                                                    precision={0.5}
                                                />
                                                <TextField
                                                    label={uiText('reviewLabel', lang)}
                                                    multiline
                                                    rows={3}
                                                    sx={{ width: 400 }}
                                                    size="small"
                                                    variant="outlined"
                                                    value={comment}
                                                    onChange={(e) => setComment(e.target.value)}
                                                />
                                            </DialogContent>
                                            <DialogActions>
                                                <button onClick={handleDialogClose} className="py-2 px-6 rounded shadow bg-white border border-red-500 hover:bg-red-100 text-red-600 uppercase">{uiText('cancelBtn', lang)}</button>
                                                <button onClick={reviewSubmitHandler} className="py-2 px-6 rounded bg-green-600 hover:bg-green-700 text-white shadow uppercase">{uiText('submitBtn', lang)}</button>
                                            </DialogActions>
                                        </Dialog>

                                        <div className="flex items-center border-b">
                                            <h1 className="px-6 py-3 text-3xl font-semibold">{product.ratings && product.ratings.toFixed(1)}<StarIcon /></h1>
                                            <p className="text-lg text-gray-500">({product.numOfReviews}) {uiText('reviewsWord', lang)}</p>
                                        </div>

                                        {viewAll ?
                                            product.reviews?.map((rev, i) => (
                                                <div className="flex flex-col gap-2 py-4 px-6 border-b" key={i}>
                                                    <Rating name="read-only" value={rev.rating} readOnly size="small" precision={0.5} />
                                                    <p>{rev.comment}</p>
                                                    <span className="text-sm text-gray-500">{uiText('byWord', lang)} {rev.name}</span>
                                                </div>
                                            )).reverse()
                                            :
                                            product.reviews?.slice(-3).map((rev, i) => (
                                                <div className="flex flex-col gap-2 py-4 px-6 border-b" key={i}>
                                                    <Rating name="read-only" value={rev.rating} readOnly size="small" precision={0.5} />
                                                    <p>{rev.comment}</p>
                                                    <span className="text-sm text-gray-500">{uiText('byWord', lang)} {rev.name}</span>
                                                </div>
                                            )).reverse()
                                        }
                                        {product.reviews?.length > 3 &&
                                            <button onClick={() => setViewAll(!viewAll)} className="w-1/3 m-2 rounded-sm shadow hover:shadow-lg py-2 bg-primary-blue text-white">{viewAll ? uiText('viewLess', lang) : uiText('viewAll', lang)}</button>
                                        }
                                    </div>

                                </div>

                            </div>

                        </div>

                        <div className="flex flex-col gap-3 mt-6">
                            {similarByCategory.length > 0 && (
                                <ProductSlider
                                    title={uiText('similarProducts', lang)}
                                    tagline={uiText('fromSameCategory', lang)}
                                    products={similarByCategory}
                                    viewAllUrl={product.categories?.[0]?.slug ? `/products?category=${product.categories[0].slug}` : "/products"}
                                />
                            )}
                            {similarByBrand.length > 0 && (
                                <ProductSlider
                                    title={`${uiText('moreFrom', lang)} ${product.brand?.name}`}
                                    tagline={uiText('youMightAlsoLike', lang)}
                                    products={similarByBrand}
                                    viewAllUrl={`/products?brand=${encodeURIComponent(product.brand?.name || "")}`}
                                />
                            )}
                        </div>

                    </main>
                </>
            )}
        </>
    );
};

export default ProductDetails;